import {
  Component,
  computed,
  contentChild,
  DestroyRef,
  ElementRef,
  inject,
  input,
  model,
  numberAttribute,
  output,
  Renderer2,
  ChangeDetectionStrategy,
} from '@angular/core';
import { Icon } from '../icon/icon';
export const FORMA_BUTTON_APPEARANCES = [
  'primary',
  'secondary',
  'tertiary',
  'text',
  'link',
  'alert',
] as const;

export type ButtonAppearance = (typeof FORMA_BUTTON_APPEARANCES)[number];

export const APPEARANCE_CLASSES: Map<ButtonAppearance, readonly string[]> = new Map([
  ['primary', ['forma-button--primary']],
  ['secondary', ['forma-button--secondary']],
  ['tertiary', ['forma-button--tertiary']],
  ['text', ['forma-button--text']],
  ['alert', ['forma-button--alert']],
]);

const DEFAULT_APPEARANCE: ButtonAppearance = 'primary';
export const BUTTON_SIZES = ['small', 'narrow', 'default'] as const;
export type ButtonSizes = (typeof BUTTON_SIZES)[number];

const normalizeAppearance = (value: ButtonAppearance | '' | null | undefined): ButtonAppearance => {
  if (APPEARANCE_CLASSES.has(value as ButtonAppearance)) {
    return value as ButtonAppearance;
  }

  return DEFAULT_APPEARANCE;
};

@Component({
  selector: 'button[formaButton], a[formaButton]',
  templateUrl: './button.html',
  styleUrls: ['./button.scss'],
  imports: [Icon],
  changeDetection: ChangeDetectionStrategy.Eager,
  host: {
    class: 'forma-button',
    '[attr.disabled]': 'getDisabledAttribute()',
    '[attr.aria-disabled]': 'isAnchor && isDisabled() ? true : null',
    '[attr.tabindex]': 'isAnchor && isDisabled() ? -1 : tabIndex()',
    '[class.forma-button-disabled]': 'disabled()',
    '[class.forma-button--pending]': 'pending()',
    '[class]': 'hostClasses()',
  },
})
export class Button {
  private readonly element = inject<ElementRef<HTMLElement>>(ElementRef).nativeElement;
  protected readonly isAnchor = this.element.tagName === 'A';
  protected readonly isDisabled = computed(() => !!(this.disabled() || this.pending()));

  size = input<ButtonSizes>('default');
  tabIndex = input<number | null, unknown>(
    this.element.hasAttribute('tabindex')
      ? numberAttribute(this.element.getAttribute('tabindex'))
      : null,
    {
      transform: (value) => (value == null ? null : numberAttribute(value)),
    },
  );
  disabled = model<boolean | null>(null);
  pending = input<boolean>(false);
  pendingText = input<string | undefined>(undefined);
  clicked = output();

  constructor() {
    if (this.isAnchor) {
      const renderer = inject(Renderer2);
      const destroyRef = inject(DestroyRef);
      for (const eventName of ['click', 'auxclick']) {
        const unlisten = renderer.listen(
          this.element,
          eventName,
          (event: MouseEvent) => {
            if (this.isDisabled()) {
              event.preventDefault();
              event.stopImmediatePropagation();
            }
          },
          { capture: true },
        );
        destroyRef.onDestroy(unlisten);
      }
    }
  }

  appearance = input<ButtonAppearance, ButtonAppearance | '' | null>(DEFAULT_APPEARANCE, {
    alias: 'formaButton',
    transform: (value: ButtonAppearance | '' | null): ButtonAppearance =>
      normalizeAppearance(value),
  });

  private iconChild = contentChild(Icon);

  protected hasIcon = computed(() => !!this.iconChild());

  protected hostClasses = computed(() => {
    const hasIcon = this.hasIcon();
    const classes: Record<string, boolean> = {
      'forma-button--small': this.size() === 'small',
      'forma-button--narrow': this.size() === 'narrow',
      'forma-button--has-icon': hasIcon,
      'forma-button--has-pending-text': !!this.pendingText(),
    };
    for (const appearanceOption of FORMA_BUTTON_APPEARANCES) {
      classes[`forma-button--${appearanceOption}`] = this.appearance() === appearanceOption;
    }
    return classes;
  });

  protected getDisabledAttribute() {
    return !this.isAnchor && this.isDisabled() ? true : null;
  }
}
